// Phase 15 · Portal Screen 1 — Order Fulfillment (secondary orders under
// this distributor, whoever booked them — SE, ASM, or the distributor
// themself). A distributor can edit (while still placed), reject, or
// fulfill an order; once fulfilled there's nothing further to do on it —
// matches the same terminal rule the rest of the app already enforces.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Truck, CheckCircle2, Clock, XCircle, Pencil, PackageCheck, Ban } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import UomSelector from "@/components/UomSelector";

const STATUS_COLORS = {
  booked:     "bg-amber-50   text-amber-700   border-amber-200",
  placed:     "bg-amber-50   text-amber-700   border-amber-200",
  approved:   "bg-sky-50     text-sky-700     border-sky-200",
  accepted:   "bg-sky-50     text-sky-700     border-sky-200",
  dispatched: "bg-indigo-50  text-indigo-700  border-indigo-200",
  delivered:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  fulfilled:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial_fulfilled: "bg-amber-50 text-amber-800 border-amber-200",
  rejected:   "bg-rose-50    text-rose-700    border-rose-200",
  cancelled:  "bg-rose-50    text-rose-700    border-rose-200",
};

function StatusChip({ status }) {
  const cls = STATUS_COLORS[status] || "bg-neutral-100 text-neutral-700 border-neutral-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}
          data-testid={`order-status-${status}`}>
      {(status === "delivered" || status === "fulfilled") && <CheckCircle2 className="w-3 h-3" />}
      {status === "dispatched" && <Truck className="w-3 h-3" />}
      {(status === "cancelled" || status === "rejected") && <XCircle className="w-3 h-3" />}
      {["booked", "placed", "approved", "accepted", "partial_fulfilled"].includes(status) && <Clock className="w-3 h-3" />}
      {status}
    </span>
  );
}

const round2 = (n) => Math.round((n || 0) * 100) / 100;

export default function PortalOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [detailFor, setDetailFor] = useState(null);   // full order+lines, or null
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingLines, setEditingLines] = useState(null);  // {lineId: qty_pcs} while editing
  const [fulfillEntries, setFulfillEntries] = useState({}); // lineId -> {uom, qty}
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // channel=secondary → only outlet orders (SE/ASM/distributor); primary
      // orders (distributor → company) live in the separate Primary screen.
      const r = await api.get("/orders?channel=secondary&page_size=100&enrich=grid");
      setOrders(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetailFor({ id });
    setEditingLines(null);
    setFulfillEntries({});
    setRejectOpen(false);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setDetailFor(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetailFor(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const transition = async (id, to, notes) => {
    setActing(a => ({ ...a, [id]: true }));
    try {
      await api.post(`/orders/${id}/status`, { to_status: to, notes: notes || `Marked ${to} by distributor` });
      toast.success(`Order marked ${to}`);
      setDetailFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(a => ({ ...a, [id]: false }));
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 3) {
      toast.error("Reason (min 3 characters) is required"); return;
    }
    await transition(detailFor.id, "rejected", rejectReason.trim());
  };

  const startEdit = () => {
    const m = {};
    (detailFor.lines || []).forEach(l => { m[l.id] = String(round2(l.qty_kg ?? 0)); });
    setEditingLines(m);
  };

  const saveEdit = async () => {
    const lines = [];
    for (const l of detailFor.lines || []) {
      const val = editingLines[l.id];
      const qty_kg = val === "" ? null : Number(val);
      const prev = round2(l.qty_kg ?? 0);
      if (qty_kg === null || round2(qty_kg) === prev) continue;
      lines.push({ line_id: l.id, qty_kg });
    }
    if (lines.length === 0) { toast.error("No changes to save"); setEditingLines(null); return; }
    setActing(a => ({ ...a, [detailFor.id]: true }));
    try {
      await api.patch(`/orders/${detailFor.id}`, { lines });
      toast.success("Order updated");
      await openDetail(detailFor.id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(a => ({ ...a, [detailFor.id]: false }));
    }
  };

  const startFulfill = () => {
    const m = {};
    (detailFor.lines || []).forEach(l => {
      // Pre-fill with the outstanding qty (ordered − already fulfilled) so
      // the common case — fulfilling the whole line in one go — needs no
      // typing at all; still fully editable for a partial fulfillment.
      const outstandingKg = round2((l.qty_kg || 0) - (l.fulfilled_qty_kg || 0));
      m[l.id] = { uom: "KG", qty: outstandingKg > 0 ? String(outstandingKg) : "" };
    });
    setFulfillEntries(m);
  };

  const saveFulfill = async () => {
    const lines = [];
    for (const l of detailFor.lines || []) {
      const e = fulfillEntries[l.id];
      if (!e || !Number(e.qty) || Number(e.qty) <= 0) continue;
      lines.push({ line_id: l.id, fulfilled_uom: e.uom, fulfilled_qty_entered: Number(e.qty) });
    }
    if (lines.length === 0) { toast.error("Enter a fulfilled quantity for at least one line"); return; }
    setActing(a => ({ ...a, [detailFor.id]: true }));
    try {
      await api.post(`/orders/${detailFor.id}/fulfill`, { lines });
      toast.success("Fulfillment recorded");
      setDetailFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(a => ({ ...a, [detailFor.id]: false }));
    }
  };

  // Edit stays available through accepted/partial_fulfilled, not just
  // pre-acceptance — matches the backend's edit_order rule (Phase 15: a
  // distributor may edit/reject/fulfill their own secondary order; only
  // once it's fulfilled is there nothing further to do on it).
  const canEdit = detailFor && ["booked", "placed", "approved", "accepted", "partial_fulfilled"].includes(detailFor.status);
  const canFulfill = detailFor && ["approved", "accepted", "partial_fulfilled"].includes(detailFor.status);
  const canReject = detailFor && ["booked", "placed", "approved", "accepted", "partial_fulfilled"].includes(detailFor.status);
  // "Mark dispatched" used to sit alongside Fulfill on an "approved" order,
  // but both do the same job here (move the order forward) — collapsed to
  // just Fulfill. "Mark delivered" stays for any order that's already
  // sitting in the legacy "dispatched" status.
  const canDeliver = detailFor && detailFor.status === "dispatched";
  const isTerminal = detailFor && ["fulfilled", "delivered", "rejected", "cancelled"].includes(detailFor.status);

  return (
    <div className="p-4 space-y-3" data-testid="portal-orders-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Order Fulfillment</h1>
        <span className="text-[11px] text-neutral-500" data-testid="orders-total">{orders.length} orders</span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && orders.length === 0 && (
        <div className="text-center py-12 text-sm text-neutral-500" data-testid="orders-empty">
          No secondary orders yet.
        </div>
      )}

      <div className="space-y-2">
        {orders.map(o => (
          <button key={o.id} type="button" onClick={() => openDetail(o.id)}
                  className="w-full text-left bg-white rounded-xl border p-3 shadow-sm space-y-1.5 hover:border-[#6D2C91]/40"
                  data-testid={`order-row-${o.order_no}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-sm">{o.order_no}</div>
                <div className="text-[11px] text-neutral-500">
                  {o.order_date} · {round2(o.total_qty_kg || 0)} kg · ₹{(o.net_amount || 0).toLocaleString("en-IN")}
                </div>
              </div>
              <StatusChip status={o.status} />
            </div>
            <div className="text-[11px] text-neutral-600 truncate">
              {o.retailer_name || "—"} <span className="text-neutral-400">·</span> by {o.user_name || "—"}
            </div>
          </button>
        ))}
      </div>

      {/* Detail / action sheet */}
      {detailFor && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-end md:items-center justify-center"
             data-testid="portal-order-detail">
          <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl flex flex-col max-h-[92vh]">
            <div className="flex items-center justify-between p-4 pb-2 shrink-0">
              <h2 className="text-base font-semibold">{detailFor.order_no || "Order"}</h2>
              <button onClick={() => setDetailFor(null)} className="text-sm text-neutral-500">Close</button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-10 text-neutral-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : (
              <div className="px-4 pb-4 space-y-3 overflow-y-auto flex-1">
                <div className="flex items-center justify-between">
                  <StatusChip status={detailFor.status} />
                  <span className="text-sm font-bold">₹{(detailFor.net_amount || 0).toLocaleString("en-IN")}</span>
                </div>
                <div className="text-xs text-neutral-500">
                  {detailFor.order_date} · {detailFor.retailer_name || detailFor.retailer?.name || "—"} · by {detailFor.user_name || detailFor.user?.name || "—"}
                </div>

                <div className="border rounded-lg divide-y divide-neutral-100">
                  {(detailFor.lines || []).map(l => {
                    const outstanding = (l.qty_pcs ?? l.qty ?? 0) - (l.fulfilled_qty_pcs || 0);
                    return (
                      <div key={l.id} className="p-2 space-y-1" data-testid={`portal-order-line-${l.sku_code_snapshot}`}>
                        <div className="flex items-center justify-between text-sm">
                          <div className="min-w-0">
                            <div className="font-mono text-[11px] text-neutral-500">{l.sku_code_snapshot}</div>
                            <div className="text-neutral-900 truncate">{l.sku_name_snapshot || "—"}</div>
                          </div>
                          <div className="text-right shrink-0 ml-2">
                            <div>{round2(l.qty_kg)} kg</div>
                            <div className="text-[11px] text-neutral-500">₹{(l.line_total || 0).toLocaleString("en-IN")}</div>
                          </div>
                        </div>
                        {l.scheme_code_snapshot && (
                          <div className="text-[11px] text-[#E5197F] font-semibold"
                               data-testid={`portal-order-line-scheme-${l.sku_code_snapshot}`}>
                            🎁 {l.scheme_code_snapshot}
                            {l.scheme_benefit_type === "free_item_note" && l.scheme_benefit_value && ` · ${l.scheme_benefit_value}`}
                            {l.scheme_benefit_type === "free_qty" && l.free_qty > 0 && ` · +${l.free_qty} pcs free`}
                            {(l.discount_amount || 0) > 0 && ` · −₹${l.discount_amount.toLocaleString("en-IN")}`}
                          </div>
                        )}
                        {l.fulfilled_qty_pcs > 0 && (
                          <div className="text-[11px] text-emerald-700">
                            ✓ Fulfilled {round2(l.fulfilled_qty_kg)} kg
                          </div>
                        )}
                        {editingLines && (
                          <div className="flex items-center gap-2 pt-1">
                            <span className="text-[11px] text-neutral-500">Qty (kg)</span>
                            <input type="number" step="0.01" min={round2(l.fulfilled_qty_kg || 0)}
                                   value={editingLines[l.id] ?? ""}
                                   onChange={e => setEditingLines(m => ({ ...m, [l.id]: e.target.value }))}
                                   className="w-24 h-8 px-2 rounded border border-neutral-200 text-sm"
                                   data-testid={`portal-edit-qty-${l.sku_code_snapshot}`} />
                          </div>
                        )}
                        {fulfillEntries[l.id] && outstanding > 0 && (
                          <div className="flex items-center gap-2 pt-1">
                            <UomSelector sku={{ allowed_uoms: ["KG", "BAG"] }}
                                         value={fulfillEntries[l.id].uom}
                                         onChange={u => setFulfillEntries(m => ({ ...m, [l.id]: { ...m[l.id], uom: u } }))}
                                         testId={`portal-fulfill-uom-${l.sku_code_snapshot}`} />
                            <input type="number" min="0" placeholder="Qty"
                                   value={fulfillEntries[l.id].qty}
                                   onChange={e => setFulfillEntries(m => ({ ...m, [l.id]: { ...m[l.id], qty: e.target.value } }))}
                                   className="w-20 h-8 px-2 rounded border border-neutral-200 text-sm"
                                   data-testid={`portal-fulfill-qty-${l.sku_code_snapshot}`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {isTerminal && (
                  <div className="text-xs text-neutral-500 text-center py-1">
                    {detailFor.status === "fulfilled" || detailFor.status === "delivered"
                      ? "Order fulfilled — no further action available."
                      : "This order is closed."}
                  </div>
                )}

                {rejectOpen && (
                  <div className="space-y-2 border border-rose-200 bg-rose-50 rounded-lg p-2">
                    <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                              placeholder="Reason for rejecting (min 3 characters)…"
                              rows={2}
                              data-testid="portal-reject-reason"
                              className="w-full text-sm rounded border border-rose-200 p-2" />
                    <div className="flex gap-2">
                      <button onClick={() => { setRejectOpen(false); setRejectReason(""); }}
                              className="flex-1 h-9 rounded-lg border border-neutral-200 text-neutral-600 text-xs font-medium">
                        Cancel
                      </button>
                      <button onClick={doReject} disabled={acting[detailFor.id]}
                              data-testid="portal-reject-confirm"
                              className="flex-1 h-9 rounded-lg bg-rose-600 text-white text-xs font-medium disabled:opacity-50">
                        {acting[detailFor.id] ? <Loader2 className="w-3.5 h-3.5 mx-auto animate-spin" /> : "Confirm reject"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!detailLoading && !isTerminal && (
              <div className="p-4 pt-2 border-t border-neutral-200 shrink-0 space-y-2">
                {editingLines ? (
                  <div className="flex gap-2">
                    <button onClick={() => setEditingLines(null)}
                            className="flex-1 h-10 rounded-lg border border-neutral-200 text-neutral-700 text-sm">
                      Cancel
                    </button>
                    <button onClick={saveEdit} disabled={acting[detailFor.id]}
                            data-testid="portal-edit-save"
                            className="flex-1 h-10 rounded-lg tulsi-gradient text-white text-sm disabled:opacity-50">
                      {acting[detailFor.id] ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : "Save changes"}
                    </button>
                  </div>
                ) : Object.keys(fulfillEntries).length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-[11px] text-neutral-500">
                      Delivering less than the full outstanding qty still closes this order — the
                      remainder is written off, not queued for a later round.
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setFulfillEntries({})}
                              className="flex-1 h-10 rounded-lg border border-neutral-200 text-neutral-700 text-sm">
                        Cancel
                      </button>
                      <button onClick={saveFulfill} disabled={acting[detailFor.id]}
                              data-testid="portal-fulfill-save"
                              className="flex-1 h-10 rounded-lg bg-emerald-600 text-white text-sm disabled:opacity-50">
                        {acting[detailFor.id] ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : "Record fulfillment"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {canEdit && (
                      <button onClick={startEdit} data-testid="portal-order-edit-btn"
                              className="flex-1 h-10 rounded-lg border border-neutral-200 text-neutral-700 text-sm flex items-center justify-center gap-1.5">
                        <Pencil className="w-4 h-4" /> Edit
                      </button>
                    )}
                    {canFulfill && (
                      <button onClick={startFulfill} data-testid="portal-order-fulfill-btn"
                              className="flex-1 h-10 rounded-lg bg-emerald-600 text-white text-sm flex items-center justify-center gap-1.5">
                        <PackageCheck className="w-4 h-4" /> Fulfill
                      </button>
                    )}
                    {canDeliver && (
                      <button onClick={() => transition(detailFor.id, "delivered")} disabled={acting[detailFor.id]}
                              data-testid="portal-order-deliver-btn"
                              className="flex-1 h-10 rounded-lg bg-emerald-600 text-white text-sm flex items-center justify-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" /> Mark delivered
                      </button>
                    )}
                    {canReject && (
                      <button onClick={() => setRejectOpen(true)} data-testid="portal-order-reject-btn"
                              className="flex-1 h-10 rounded-lg border border-rose-200 text-rose-600 text-sm flex items-center justify-center gap-1.5">
                        <Ban className="w-4 h-4" /> Reject
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
